<script setup lang="ts">
    import { toDateOnly } from '@shared/utils/formatDateTime';
    import { reactive, watch } from 'vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { findClientCompany, getAccountsReceivable, ClientCompanySearchResult, AccountsReceivableEntry } from './Billing.access';

    const defaultFrom = new Date();
    defaultFrom.setMonth(defaultFrom.getMonth() - 1);

    const state = reactive<{
        searchText: string;
        searchResults: ClientCompanySearchResult[];
        selectedCompany: ClientCompanySearchResult | null;
        from: string;
        to: string;
        entries: AccountsReceivableEntry[];
        loading: boolean;
        searchLoading: boolean;
    }>({
        searchText: '',
        searchResults: [],
        selectedCompany: null,
        from: toDateOnly(defaultFrom),
        to: toDateOnly(),
        entries: [],
        loading: false,
        searchLoading: false,
    });

    let searchTimeout: ReturnType<typeof setTimeout> | null = null;

    function onSearchInput(): void {
        const val = state.searchText;
        if (searchTimeout) clearTimeout(searchTimeout);
        if (!val || val.length < 2) {
            state.searchResults = [];
            return;
        }
        searchTimeout = setTimeout(async () => {
            state.searchLoading = true;
            try {
                state.searchResults = await findClientCompany(val);
            } catch {
                bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to search companies' });
            } finally {
                state.searchLoading = false;
            }
        }, 300);
    }

    async function selectCompany(company: ClientCompanySearchResult): Promise<void> {
        state.selectedCompany = company;
        state.searchText = company.BillingName || company.CompanyName;
        state.searchResults = [];
        await loadAccountsReceivable();
    }

    async function loadAccountsReceivable(): Promise<void> {
        if (!state.selectedCompany) return;
        state.loading = true;
        try {
            state.entries = await getAccountsReceivable(state.selectedCompany.ServiceSubscriptionId, state.from, state.to);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to load accounts receivable' });
        } finally {
            state.loading = false;
        }
    }

    watch(() => [state.from, state.to], () => {
        if (state.selectedCompany) loadAccountsReceivable();
    });

    function formatDate(dateStr: string): string {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString();
    }

    interface CurrencySum {
        amount: number;
        currencyCode: string;
    }

    function sums(): CurrencySum[] {
        const map: Record<string, number> = {};
        for (const e of state.entries) {
            if (!map[e.CurrencyCode]) map[e.CurrencyCode] = 0;
            map[e.CurrencyCode] += e.Amount + e.Vat;
        }
        return Object.entries(map).map(([currencyCode, amount]) => ({ currencyCode, amount }));
    }
</script>

<template>
    <div class="row">
        <div class="col-md-3">
            <div class="form-group search-group">
                <label>Find recipient</label>
                <div class="input-group">
                    <span class="input-group-addon">
                        <span class="glyphicon glyphicon-search" aria-hidden="true"></span>
                    </span>
                    <input
                        type="text"
                        class="form-control"
                        placeholder="Search"
                        v-model="state.searchText"
                        @input="onSearchInput" />
                </div>

                <div v-if="state.searchResults.length > 0" class="dropdown active">
                    <ul class="dropdown-results dropdown-menu">
                        <li v-for="company in state.searchResults" :key="company.ServiceSubscriptionId">
                            <a href="#" class="text-wrap" @click.prevent="selectCompany(company)">
                                {{ company.BillingName || company.CompanyName || '[unknown]' }}
                                <span v-if="company.BillingName && company.CompanyName"> ({{ company.CompanyName }})</span>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>From</label>
                <input
                    type="date"
                    class="form-control"
                    :value="state.from"
                    @change="state.from = ($event.target as HTMLInputElement).value" />
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>To</label>
                <input
                    type="date"
                    class="form-control"
                    :value="state.to"
                    @change="state.to = ($event.target as HTMLInputElement).value" />
            </div>
        </div>
    </div>

    <div v-if="state.selectedCompany" class="row m-md-b">
        <div class="col-md-3">
            <span class="label-look d-block">Company name:</span>
            <p>{{ state.selectedCompany.CompanyName }}</p>
        </div>
        <div class="col-md-3">
            <span class="label-look d-block">Billing address:</span>
            <p>{{ state.selectedCompany.BillingAddress }}</p>
        </div>
        <div class="col-md-3">
            <span class="label-look d-block">Service Subscription Id:</span>
            <p>{{ state.selectedCompany.ServiceSubscriptionId }}</p>
        </div>
    </div>

    <div v-if="state.loading">Loading...</div>

    <div v-if="state.selectedCompany" class="table-responsive">
        <table class="table table-hover table-condensed">
            <thead class="small">
                <tr>
                    <th>Ledger type</th>
                    <th>Invoice no</th>
                    <th>Invoice/payment date</th>
                    <th class="text-right">Amount</th>
                    <th>Status</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="entry in state.entries" :key="entry.SourceId">
                    <td>{{ entry.Type }}</td>
                    <td>{{ entry.InvoiceNumber }}</td>
                    <td>{{ formatDate(entry.Date) }}</td>
                    <td class="text-right">{{ (entry.Amount + entry.Vat).toFixed(2) }} {{ entry.CurrencyCode }}</td>
                    <td>{{ entry.Status }}</td>
                </tr>
            </tbody>
            <tfoot v-if="state.entries.length > 0">
                <tr>
                    <th></th>
                    <th></th>
                    <th class="text-right">Sum</th>
                    <th class="text-right">
                        <span v-for="s in sums()" :key="s.currencyCode">
                            {{ s.amount.toFixed(2) }} {{ s.currencyCode }}<br />
                        </span>
                    </th>
                    <th></th>
                </tr>
            </tfoot>
        </table>
    </div>
</template>

<script lang="ts">
    export default { name: 'BillingAccountsReceivable' };
</script>

<style></style>
